/**
 * Delivery-location verification for the admin `checkDelivery` path.
 *
 * MIRRORS gearfocus-api `functions/src/orders/utils/deliveryLocationMatch.ts`
 * (api#396). The two repos cannot share a module, so the logic is duplicated
 * intentionally; keep them in step. See that file for the full rationale.
 *
 * Summary: FedEx tracking events from ShipEngine never carry a postal_code, so
 * the postal-match check could never verify a FedEx delivery and support's
 * manual "open the order and track it" workaround was broken for FedEx. This
 * falls back to city_locality + state_province (which FedEx populates) when the
 * carrier omits the postal code, and never advances a delivery it cannot
 * verify.
 */

export interface LocationFields {
  postal_code?: string | null;
  city_locality?: string | null;
  state_province?: string | null;
}

export type LocationVerdict =
  | { verified: true; via: 'postal' | 'city_state' }
  | {
      verified: false;
      reason: 'location_mismatch' | 'location_unverifiable';
    };

export const normalizePostal = (
  value?: string | null
): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.split('-')[0].replace(/\s+/g, '').toUpperCase();
  return normalized === '' ? undefined : normalized;
};

export const normalizeLocationText = (
  value?: string | null
): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().replace(/\s+/g, ' ').toUpperCase();
  return normalized === '' ? undefined : normalized;
};

/**
 * City+state fallback is scoped to FedEx (Bill's ruling 2026-08-26). Mirrors
 * gearfocus-api.
 */
export const carrierAllowsCityStateFallback = (
  carrier?: string | null
): boolean =>
  typeof carrier === 'string' && carrier.trim().toLowerCase() === 'fedex';

/**
 * Pick the tracking event to verify against. City+state is read ONLY from
 * events[0] (the delivery scan) — an earlier in-transit scan can carry a
 * different city and city+state is too coarse to tell that apart. We reach
 * back only for an exact postal code (which a mid-route hub cannot fake into a
 * match), and otherwise return events[0] so an unverifiable delivery fails
 * safe. Mirrors gearfocus-api (api#396 review).
 */
export const selectVerificationEvent = <T extends LocationFields>(
  events: readonly T[] | undefined | null
): T | undefined => {
  if (!events || events.length === 0) return undefined;
  const head = events[0];
  if (
    normalizePostal(head.postal_code) !== undefined ||
    (normalizeLocationText(head.city_locality) !== undefined &&
      normalizeLocationText(head.state_province) !== undefined)
  )
    return head;
  const withPostal = events.find(
    (event) => normalizePostal(event.postal_code) !== undefined
  );
  return withPostal ?? head;
};

/**
 * Postal when both sides have one; city+state fallback for carriers that omit
 * the postal code. A genuine location difference is a mismatch; no comparable
 * signal at all is unverifiable. Verification is widened, never removed.
 */
export const verifyDeliveryLocation = (
  orderAddress: LocationFields | undefined | null,
  trackingEvent: LocationFields | undefined | null,
  carrier?: string | null
): LocationVerdict => {
  const orderPostal = normalizePostal(orderAddress?.postal_code);
  const trackingPostal = normalizePostal(trackingEvent?.postal_code);

  if (orderPostal !== undefined && trackingPostal !== undefined) {
    return orderPostal === trackingPostal
      ? { verified: true, via: 'postal' }
      : { verified: false, reason: 'location_mismatch' };
  }

  // City+state fallback is FedEx-only; any other postal-less carrier is left
  // unverifiable (Bill's ruling 2026-08-26).
  if (!carrierAllowsCityStateFallback(carrier)) {
    return { verified: false, reason: 'location_unverifiable' };
  }

  const orderCity = normalizeLocationText(orderAddress?.city_locality);
  const orderState = normalizeLocationText(orderAddress?.state_province);
  const trackingCity = normalizeLocationText(trackingEvent?.city_locality);
  const trackingState = normalizeLocationText(trackingEvent?.state_province);

  const canCompareCityState =
    orderCity !== undefined &&
    orderState !== undefined &&
    trackingCity !== undefined &&
    trackingState !== undefined;

  if (canCompareCityState) {
    return orderCity === trackingCity && orderState === trackingState
      ? { verified: true, via: 'city_state' }
      : { verified: false, reason: 'location_mismatch' };
  }

  return { verified: false, reason: 'location_unverifiable' };
};
